

import { Component, Vue, Watch, Prop, Ref } from "vue-property-decorator"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"
import { IntegrationColumn, IntegrationSettings, Integration, InFieldConfiguration, IntegrationEventMapping, SyncObjectType } from "@/types/integration"
import { Field, TemplateDetails } from "@/types"
import { CaseColumn } from "@/types/caseColumns"
import DeleteButton from "@/components/New/Page/PageComponents/DeleteButton.vue"
import { BModal, BButton } from "bootstrap-vue"

import FieldMappingRow from "./FieldMappingRow.vue"
import { State } from "vuex-class"
import DataCache from "@/caching/caching"
import { FieldTypeId } from "@/types/fieldTypes"

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"delete-btn": DeleteButton,
		"field-mapping-row": FieldMappingRow
	}
})
export default class FieldMapping extends Vue {
	@Ref() deleteConfirmModal!: BModal

	@Prop()
	value!: { [key: string]: string }

	@Prop({ default: () => [] })
	fields!: IntegrationColumn[];

	@Prop({ default: () => [] })
	casesFields!: Field[];

	@Prop({ default: () => [] })
	caseColumns!: CaseColumn[]

	@Prop()
	createField!: (field: IntegrationColumn) => Promise<Field>

	@Prop({ default: true })
	allowCreateFields!: boolean

	@Prop({ type: Boolean, default: false })
	allowCustomFields!: boolean

	@Prop({ type: Boolean, default: true })
	allowAddMapping!: boolean

	@Prop({ type: Boolean, default: true })
	allowDeleteMapping!: boolean

	@Prop({ type: Boolean, default: true })
	allowFieldSelection!: boolean

	@Prop({ default: () => ({}) })
	inFieldConfiguration!: { [key: string]: InFieldConfiguration }

	@Prop()
	integration!: Integration

	@Prop({ type: Object, default: null })
	mappingsForEvent!: IntegrationEventMapping | null

	@Prop({ type: Boolean, default: false })
	hideTitle!: boolean

	@Prop({ type: Boolean, default: false })
	showAllFields!: boolean

	@Prop({ type: Boolean, default: false })
	requirePrimaryKey!: boolean

	@Prop({ type: String, default: null })
	primaryKey!: string | null

	/* Vuex */
	@State private readonly templateCache!: DataCache<TemplateDetails>

	renamingCustomMappings: { [key: string]: boolean } = {}

	deletingCustomMapping: IntegrationColumn | null = null

	get casesColumnsAndFields (): CaseColumn[] {
		return [
			...this.caseColumns,
			...this.casesFields.map(f => {
				return {
					key: f.id!,
					label: f.title,
					dataType: f.dataType!,
					fieldExtensions: f.extensions,
					dynamicField: true
				}
			})
		]
	}

	get mappedKeys (): string[] {
		return this.value ? Object.keys(this.value) : []
	}

	/**
	 * @summary Returns the fields that can be mapped to the event mapping's selected Template.
	 * Does not display fields hidden on the Template. If editing global mappings, displays all allowed fields.
	 */
	private get fieldsForEventMapping (): CaseColumn[] {
		if (this.showAllFields) {
			return this.casesColumnsAndFields.filter(this.fieldIsAllowed)
		}

		const eventMappingTemplate = this.mappingsForEvent?.templateId
			? this.templateCache.getById(this.mappingsForEvent.templateId)!
			: null
		if (!eventMappingTemplate) return this.casesColumnsAndFields.filter(this.fieldIsAllowed)

		const templateFieldIds = eventMappingTemplate?.fields
			? Object.values(eventMappingTemplate.fields).filter(field => !!field.field).map(f => f.field.id)
			: []

		return this.casesColumnsAndFields.filter(field =>
			((!field.dynamicField && !eventMappingTemplate.hiddenFields?.includes(field.key)) ||
				templateFieldIds.includes(field.key)) && this.fieldIsAllowed(field)
		)
	}

	private get supportCustomFields (): boolean {
		return this.integration?.inbound?.supportCustomFields ?? false
	}

	private get supportColumnFieldMappings (): boolean {
		return this.integration?.csvSyncOptions?.supportCsvSync ?? false
	}

	private get allowedDataTypes (): string[] {
		const allowedDataTypes = [
			FieldTypeId.Text,
			FieldTypeId.TextArea,
			FieldTypeId.Number,
			FieldTypeId.Checkbox,
			FieldTypeId.List,
			FieldTypeId.ListMultiple,
			FieldTypeId.UserRole,
			FieldTypeId.ExternalLink,
			FieldTypeId.Date,
			FieldTypeId.DateTime
		]

		if (this.integration.supportsFiles) {
			allowedDataTypes.push(FieldTypeId.FileOnly)
		}

		if (this.integration.csvSyncOptions?.supportCsvSync &&
			this.integration.csvSyncOptions?.syncObjectType === SyncObjectType.DataSource) {
			allowedDataTypes.push(FieldTypeId.Dollar)
		}

		return allowedDataTypes
	}

	private get allFields (): IntegrationColumn[] {
		return [...this.fields, ...this.customFieldMappings]
	}

	private get customFieldMappings (): IntegrationColumn[] {
		if (!this.value) return []

		const customFields = Object.keys(this.value).filter(key => !this.fields.find(f => f.key === key))
		this.renamingCustomMappings = Object.keys(this.value).toDictionaryMapValue(k => k, k => this.renamingCustomMappings[k] ?? false)
		return customFields.map(key => this.mapCustomField(key))
	}

	private get errorLabel (): string | null {
		if (this.requirePrimaryKey && this.primaryKey === null) {
			return "Primary column not selected"
		}
		return null
	}

	private get helpIconText (): string | null {
		if (this.requirePrimaryKey && this.primaryKey === null) {
			return `
				A unique primary column must be selected to identify items for update, removal, or addition during mapping.<br/>
				<br/>Please Ensure That: </strong>
				<br/>- The primary column is selected.
				<br/>- The values in the primary column are unique.
			`
		}
		return null
	}

	async mounted () {
		if (!this.value) {
			this.$emit("input", {})
		}

		await Promise.allSettled([
			this.templateCache.fetchIfNeeded()
		])
	}

	matchingFields (field: IntegrationColumn): CaseColumn[] {
		return field.dataType !== "any"
			? this.casesColumnsAndFields.filter(f => f.dataType === field.dataType)
			: this.casesColumnsAndFields.filter(f =>
				!!f.dataType &&
				this.fieldIsAllowed(f)
			)
	}

	private fieldIsAllowed (field: CaseColumn): boolean {
		return this.allowedDataTypes.includes(field.dataType) || this.fieldIsOverridableCalculatedDateField(field)
	}

	fieldIsOverridableCalculatedDateField (field: CaseColumn): boolean {
		return field.dataType === "calculated" && field.fieldExtensions?.allowOverride === true &&
			!!field.fieldExtensions?.dateField && field.fieldExtensions.dateField.length > 0
	}

	fieldValueSet (field: IntegrationColumn, fieldId: string) {
		this.$emit("input", {
			...this.value,
			[field.key]: fieldId
		})

		// Delete fields In field Configuration
		if (this.inFieldConfiguration) {
			delete this.inFieldConfiguration[field.key]
		}
	}

	async createNewField (field: IntegrationColumn) {
		const fieldId = await this.createField(field)
		this.$emit("input", {
			...this.value,
			[field.key]: fieldId
		})
	}

	/**
	 * Returns the field Mapped to the IntegrationColumn Type
	 * @param fieldMappingKey key of the mapped field
	 */
	mapCustomField (fieldMappingKey: string): any {
		if (!fieldMappingKey) return {}

		const fieldKey = this.value[fieldMappingKey]
		const field = this.casesColumnsAndFields.find(f => f.key === fieldKey)

		let dataType = field?.dataType ? field?.dataType : "any"
		let fieldExtensions = field?.fieldExtensions ? field?.fieldExtensions : null

		let mappedField: IntegrationColumn = {
			key: fieldMappingKey,
			title: fieldMappingKey,
			dataType: dataType,
			canEdit: true
		}

		// Include fieldExtensions if it exists
		if (fieldExtensions) {
			mappedField.fieldExtensions = fieldExtensions
		}
		return mappedField
	}

	async addCustomFieldMapping () {
		let newKey = "CustomFieldMapping"
		let newKeyNum = 1
		while (this.allFields.find(f => f.key === newKey + newKeyNum.toString())) {
			newKeyNum++
		}
		newKey += newKeyNum.toString()

		const newMapping = {
			...this.value,
			[newKey]: null
		}
		this.renamingCustomMappings[newKey] = true
		this.$emit("input", newMapping)

		await this.$nextTick()
	}

	deleteCustomFieldMapping (field: IntegrationColumn | null) {
		if (!field) return

		const newValue = { ...this.value }
		delete newValue[field.key]

		// Delete the field's in-field configuration
		if (this.inFieldConfiguration) {
			delete this.inFieldConfiguration[field.key]
		}

		this.$emit("input", newValue)
	}

	async renameCustomMapping (field: IntegrationColumn, newKey: string) {
		const mappingValue = this.value[field.key]
		const newValue = { ...this.value }
		delete newValue[field.key]
		newValue[newKey] = mappingValue

		// Update In Field Configurations
		if (this.inFieldConfiguration) {
			this.inFieldConfiguration[newKey] = this.inFieldConfiguration[field.key]
			delete this.inFieldConfiguration[field.key]
		}

		this.$emit("input", newValue)
	}

	startDelete (field: IntegrationColumn) {
		this.deletingCustomMapping = field
		this.deleteConfirmModal.show()
	}

	private setPrimaryKey (key: string) {
		this.$emit("setPrimaryKey", key)
	}
}

